import { useEffect, useState } from 'react';
import { Link, NavLink, useSearchParams, useLocation } from 'react-router-dom';
import { useCart } from '../shared/context/CartContext';
import { useWishlist } from '../shared/context/WishlistContext';
import { useAuth } from '../shared/context/AuthContext';
import { settingsApi, categoryApi } from '../shared/api/endpoints';
import TransparentLogo from '../shared/components/TransparentLogo';
import { resolveSiteMedia } from '../shared/utils/mediaUrl';
import { buildReturnPath } from '../shared/utils/authRedirect';
import { sampleImageTopTone } from '../shared/utils/sampleImageColor';
import defaultLogo from '../shared/assets/logo.png';
import './Navbar.css';

function IconUser() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.7" />
      <path d="M5 19.5c1.8-3.2 4.2-4.8 7-4.8s5.2 1.6 7 4.8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

function IconHeart() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 20s-7-4.4-7-9.2A3.8 3.8 0 0 1 12 7.2a3.8 3.8 0 0 1 7 3.6C19 15.6 12 20 12 20Z"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconBag() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M4 6h2l1.4 9.2a1.5 1.5 0 0 0 1.5 1.3h8.4a1.5 1.5 0 0 0 1.5-1.2L20 8H7" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="10" cy="20" r="1.2" fill="currentColor" />
      <circle cx="17" cy="20" r="1.2" fill="currentColor" />
    </svg>
  );
}

function readHeroImageUrl() {
  const slide = document.querySelector('.hero-banner__slide');
  if (!slide) return '';
  const bg = getComputedStyle(slide).backgroundImage || '';
  const match = bg.match(/url\(["']?(.*?)["']?\)/i);
  return match?.[1] || '';
}

export default function Navbar({ overHero = false }) {
  const { count: cartCount } = useCart();
  const { count: wishCount } = useWishlist();
  const { isLoggedIn, user, logout } = useAuth();
  const location = useLocation();
  const [logoSrc, setLogoSrc] = useState(defaultLogo);
  const [scrolled, setScrolled] = useState(false);
  const [categories, setCategories] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [tone, setTone] = useState('dark'); // dark = dark text; light = white text
  const [params] = useSearchParams();
  const activeCategory = params.get('category') || '';

  const onHomeHero = overHero && location.pathname === '/' && !scrolled;

  useEffect(() => {
    settingsApi
      .get()
      .then((res) => {
        const s = res.data.data;
        const src = resolveSiteMedia(s, 'brandLogo') || resolveSiteMedia(s, 'introLogo');
        if (src) setLogoSrc(src);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    categoryApi
      .list({ type: 'main' })
      .then((res) => setCategories(res.data.data || []))
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 36);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!onHomeHero) {
      setTone('dark');
      return undefined;
    }

    let alive = true;
    const sync = async () => {
      const url = readHeroImageUrl();
      const result = await sampleImageTopTone(url);
      if (alive) setTone(result.tone);
    };

    sync();
    const id = setInterval(sync, 1200);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [onHomeHero, location.pathname]);

  const catHref = (c) => `/products?category=${c._id}`;
  const isCatActive = (c) =>
    activeCategory === c._id ||
    activeCategory.toLowerCase() === c.slug ||
    activeCategory.toLowerCase() === c.name.toLowerCase();

  const navClass = [
    'nv-nav',
    onHomeHero ? 'nv-nav--hero' : 'nv-nav--solid',
    onHomeHero ? `nv-nav--tone-${tone}` : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <header className={navClass}>
        <div className="nv-nav__line" aria-hidden />
        <div className="nv-nav__inner">
          <nav className="nv-nav__left" aria-label="Categories">
            {categories.length === 0 ? (
              <Link to="/products" className="nv-nav__cat">
                Shop
              </Link>
            ) : (
              categories.map((c) => (
                <NavLink
                  key={c._id}
                  to={catHref(c)}
                  className={() => `nv-nav__cat${isCatActive(c) ? ' active' : ''}`}
                >
                  {c.name}
                </NavLink>
              ))
            )}
          </nav>

          <Link to="/" className="nv-nav__brand" aria-label="NAYAVARNAM home">
            <TransparentLogo src={logoSrc} alt="NAYAVARNAM" className="nv-nav__logo" />
          </Link>

          <nav className="nv-nav__right">
            <button
              type="button"
              className="nv-nav__burger"
              aria-label="Open categories"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((o) => !o)}
            >
              <span />
              <span />
            </button>
            {isLoggedIn ? (
              <div className="nv-nav__account">
                <Link to="/account" className="nv-nav__action" title={user?.name || 'Account'}>
                  <IconUser />
                  <span>{user?.name?.split(' ')[0] || 'Account'}</span>
                </Link>
                <button type="button" className="nv-nav__logout" onClick={logout}>
                  Logout
                </button>
              </div>
            ) : (
              <Link to="/account" state={{ from: buildReturnPath(location) }} className="nv-nav__action">
                <IconUser />
                <span>Account</span>
              </Link>
            )}
            <Link to="/wishlist" className="nv-nav__action nv-nav__icon-link">
              <IconHeart />
              <span>Wishlist</span>
              {wishCount > 0 && <em className="nv-nav__badge">{wishCount}</em>}
            </Link>
            <Link to="/cart" className="nv-nav__action nv-nav__icon-link">
              <IconBag />
              <span>Cart</span>
              {cartCount > 0 && <em className="nv-nav__badge">{cartCount}</em>}
            </Link>
          </nav>
        </div>

        {menuOpen && (
          <div className="nv-nav__drawer">
            {categories.map((c) => (
              <Link
                key={c._id}
                to={catHref(c)}
                className={isCatActive(c) ? 'is-active' : ''}
                onClick={() => setMenuOpen(false)}
              >
                {c.name}
              </Link>
            ))}
            <Link to="/products" onClick={() => setMenuOpen(false)}>
              All pieces
            </Link>
          </div>
        )}
      </header>
      {!onHomeHero && <div className="nv-nav__spacer" aria-hidden />}
    </>
  );
}
